/**
 * Nombre de comptes — ligne, une seule série, issue de `daily_snapshots`.
 *
 * ⚠️ Série potentiellement TROUÉE, à la différence des séries d'activité. Un
 * jour absent signifie qu'aucun snapshot n'a été pris ce jour-là (sync en
 * échec), et cette donnée est définitivement perdue : la prod ne conserve aucun
 * historique d'abonnement et supprime les comptes en dur. `connectNulls` reste
 * donc à `false` — une interruption du trait dit « pas de mesure », là où un
 * trait continu inventerait une valeur, et où un zéro afficherait un
 * effondrement du nombre de comptes qui n'a jamais eu lieu.
 */

import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { useChartMarkers } from '../hooks/useChartMarkers';
import { formatCompact, formatDayLong, formatDayShort, formatInteger } from '../lib/format';
import { countMissingDays, type NullableAccountsPoint } from '../lib/series';
import { AXIS_STYLE, COLORS, LINE_WIDTH } from '../lib/theme';
import type { DataColumn } from '../types';
import { ChartCard } from './Card';
import { ChartTooltip } from './ChartTooltip';
import { MarkerStrip } from './MarkerStrip';

const columns: ReadonlyArray<DataColumn<NullableAccountsPoint>> = [
  { key: 'date', header: 'Jour', render: (row) => formatDayLong(row.date) },
  {
    key: 'totalAccounts',
    header: 'Comptes',
    numeric: true,
    // Un tiret et non un zero : ce jour-la, personne n'a compte.
    render: (row) => (row.totalAccounts === null ? '—' : formatInteger(row.totalAccounts)),
  },
];

/**
 * Mode `growth` et non `level` : le nombre de comptes est un CUMUL. Comparer
 * les moyennes d'avant et d'après donnerait « en hausse » même si les
 * inscriptions s'étaient arrêtées net le jour du marqueur. Ce qui se compare
 * ici, c'est la pente — les comptes gagnés par jour.
 */
const MARKER_CONFIG = {
  value: (row: NullableAccountsPoint) => row.totalAccounts,
  mode: 'growth',
  unit: 'count',
  higherIsBetter: true,
  scope: 'analytics',
} as const;

export function AccountsChart({ data }: { data: readonly NullableAccountsPoint[] }) {
  const missing = countMissingDays(data);
  const markers = useChartMarkers(data, MARKER_CONFIG);

  return (
    <ChartCard
      title="Nombre de comptes"
      subtitle={
        missing > 0
          ? `Snapshot quotidien — ${missing} jour${missing > 1 ? 's' : ''} sans mesure, le trait s'interrompt`
          : 'Snapshot quotidien — une interruption du trait signalerait un jour non mesuré'
      }
      emptyMessage="Aucun snapshot sur cette période. Le premier arrive au lendemain du premier sync."
      rows={data}
      columns={columns}
      footer={<MarkerStrip items={markers.items} />}
    >
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data as NullableAccountsPoint[]} margin={{ top: 4, right: 12, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={COLORS.grid} vertical={false} />
          <XAxis
            dataKey="date"
            tickFormatter={formatDayShort}
            tick={{ fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize }}
            axisLine={{ stroke: COLORS.grid }}
            tickLine={false}
            minTickGap={40}
          />
          <YAxis
            tickFormatter={formatCompact}
            tick={{ fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize }}
            axisLine={false}
            tickLine={false}
            width={52}
            // Un cumul de comptes varie peu d'un jour a l'autre : force a
            // partir de zero, la courbe serait une droite plate. L'axe se cale
            // donc sur les donnees, avec une marge relative pour que le trait
            // ne vienne pas lecher le bord haut du cadre.
            domain={[
              (min: number) => Math.floor(min * 0.998),
              (max: number) => Math.ceil(max * 1.002),
            ]}
          />
          <Tooltip
            cursor={{ stroke: COLORS.textMuted, strokeWidth: 1 }}
            content={
              <ChartTooltip labelFormatter={formatDayLong} valueFormatter={formatInteger} />
            }
          />
          {markers.lines}
          <Line
            isAnimationActive={false}
            type="monotone"
            dataKey="totalAccounts"
            name="Comptes"
            stroke={COLORS.series1}
            strokeWidth={LINE_WIDTH}
            dot={false}
            connectNulls={false}
            activeDot={{ r: 4, fill: COLORS.series1, stroke: COLORS.surface, strokeWidth: 2 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
